import React, { useState, useRef, useEffect } from "react"
import "./single-dropdown.css"
import { NotificationPayload, PubSub, TopicsEnum } from "../../../src/utils/pubsub"

interface MenuItem {
  label?: string
  icon?: string // optional, always shown on the left
  isDivider?: boolean
  isDestructive?: boolean
  action?: () => void
}

interface SingleDropdownProps {
  triggerText: string
  triggerIcon?: React.ReactNode
  triggerIconPosition?: 'left' | 'right'
  align?: string
  menuItems: MenuItem[]
}

const SingleDropdown: React.FC<SingleDropdownProps> = ({
  triggerText,
  triggerIcon,
  triggerIconPosition = 'left',
  menuItems,
  align = 'left',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const [selectedLabel, setSelectedLabel] = useState<string>(triggerText)

  // selectedLabel is only ever seeded from triggerText on mount -- it never re-synced
  // when the prop itself changed later (e.g. a live count updating, like "All (6)" -> "All
  // (0)" once a search matches nothing), so the trigger button froze at whatever label/count
  // it first mounted with while the open menu (built fresh from menuItems every render) kept
  // showing the correct value. This affects every consumer of this shared component, not
  // just one page -- resync whenever the live prop changes.
  //
  // Guarded, not a plain assignment: some consumers pass a bare, count-less `triggerText`
  // (e.g. "Open") while their own `menuItems` labels carry a count ("Open (3)") -- for
  // those, the click handler below sets the richer `item.label` immediately, and the
  // parent's next render then supplies the count-less `triggerText` for that same
  // selection. A plain resync would immediately clobber the richer label back down to
  // the bare title, which both loses the count *and* breaks the `selectedLabel ===
  // item.label` match used for the open menu's selected/disabled state below. Only
  // resync when `triggerText` describes a genuinely different selection than what's
  // currently shown (i.e. the current label isn't just that same title plus a "(n)"
  // suffix already applied by the click handler).
  useEffect(() => {
    setSelectedLabel((prev) =>
      prev === triggerText || prev.startsWith(`${triggerText} (`) ? prev : triggerText
    );
  }, [triggerText]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);
  




  return (
    <div id="singleDropdown" className="single-dropdown-menu-new" ref={menuRef}>
      <button className="single-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        {triggerIcon && triggerIconPosition === "left" && (
          <span className={`trigger-icon left ${isOpen ? 'open' : ''}`}>{triggerIcon}</span>
        )}
        <span>{selectedLabel}</span>
        {triggerIcon && triggerIconPosition === "right" && (
           <span className={`trigger-icon right ${isOpen ? 'open' : ''}`}>{triggerIcon}</span>
        )}
      </button>

      {isOpen && (
        <div className={`single-dropdown-content ${align === 'left' ? 'left' : ''}`}>
          {menuItems.map((item, index) =>
            item.isDivider ? (
              <div key={`divider-${index}`} className="single-dropdown-separator"></div>
            ) : (
              <button
                key={item.label || `item-${index}`}
                className={`single-dropdown-item ${item.isDestructive ? "single-dropdown-item-destructive" : ""} ${
                    selectedLabel === item.label ? "selected" : ""
                }`}
                disabled={selectedLabel === item.label} // disable button if selected
                onClick={() => {
                    if (selectedLabel !== item.label) { // only allow if not selected
                    setSelectedLabel(item.label || triggerText)
                    item.action?.()
                    setIsOpen(false)
                    }
                }}
                >
                {item.icon && (
                    <img
                    src={item.icon}
                    alt={item.label}
                    width={16}
                    height={16}
                    className="single-dropdown-item-icon left"
                    />
                )}
                <span>{item.label}</span>
                </button>
            )
          )}
        </div>
      )}
    </div>
  )
}

export default SingleDropdown
